import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { useState, useEffect } from "react";

function HomePage() {
  const { language } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);

  // تحديد نوع الجهاز
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 768;
      
      setIsMobile(isMobileDevice || isTouchDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // محتوى محسّن للهواتف المحمولة
  const content = {
    en: {
      welcome: "Your University Courses, Simplified",
      subtitle: "Get the best explanations for your college subjects from top professors",
      browseBtn: "Browse Courses",
      stats: [
        { value: "6+", label: "Courses Available" },
        { value: "5+", label: "Expert Instructors" }
      ],
      features: [
        {
          title: "Professors You Know",
          desc: "Courses taught by your actual university lecturers",
          icon: "👨‍🏫"
        },
        {
          title: "Study Smarter",
          desc: "Clear explanations for difficult concepts",
          icon: "📚"
        },
        {
          title: "Exam Ready",
          desc: "Focus on what's important for your tests",
          icon: "✏️"
        }
      ],
      cta: "Struggling with a course? We can help!",
      quickStart: "Quick Start",
      tapToExplore: "Tap to explore courses"
    },
    ar: {
      welcome: "محاضراتك، بطريقة أبسط",
      subtitle: "احصل على أفضل الشروحات لموادك من محاضرين متميزين",
      browseBtn: "استعرض المواد",
      stats: [
        { value: "6+", label: "مواد متاحة" },
        { value: "٥+", label: "محاضرين متخصصين" }
      ],
      features: [
        {
          title: "معلمين تعرفهم",
          desc: "مواد تدرس بواسطة محاضرين من مجتمعك",
          icon: "👨‍🏫"
        },
        {
          title: "ادرس بذكاء",
          desc: "شروحات واضحة لأصعب المفاهيم",
          icon: "📚"
        },
        {
          title: "جاهز للإمتحان",
          desc: "ركز على ما يهمك في الاختبارات",
          icon: "✏️"
        }
      ],
      cta: "تواجه صعوبة في مادة ما؟ نحن هنا لمساعدتك!",
      quickStart: "ابدأ الآن",
      tapToExplore: "اضغط لاستكشاف المواد"
    }
  };

  const t = content[language];

  // تحسينات للأنيميشن على الأندرويد
  const mobileAnimationConfig = {
    initial: { opacity: 0, y: isMobile ? 10 : 20 },
    animate: { opacity: 1, y: 0 },
    transition: { 
      duration: isMobile ? 0.4 : 0.6,
      ease: "easeOut"
    }
  };

  return (
    <div 
      className="min-h-screen bg-white text-gray-900 overflow-x-hidden"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      style={{ WebkitOverflowScrolling: 'touch' }} // تحسين التمرير على iOS/Android
    >
      {/* Hero Section - محسّن للهواتف */}
      <section className="relative overflow-hidden">
        {/* Background Gradient - مبسط للأداء */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-blue-50"></div>
        
        {/* Grid Pattern - مخفي على الهواتف لتحسين الأداء */}
        {!isMobile && (
          <div className="absolute inset-0 opacity-5 [background-image:linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        )}
        
        <div className="container mx-auto px-4 sm:px-6 py-16 sm:py-20 md:py-32 relative z-10">
          <motion.div 
            {...mobileAnimationConfig}
            className="max-w-4xl mx-auto text-center"
          >
            {/* العنوان محسّن للهواتف */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 leading-tight px-2">
              {t.welcome}
            </h1>
            
            {/* الوصف مبسط للهواتف */}
            <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto px-4 leading-relaxed">
              {t.subtitle}
            </p>
            
            {/* زر CTA محسّن للمس */}
            <motion.div
              whileHover={!isMobile ? { scale: 1.03 } : {}}
              whileTap={{ scale: 0.95 }}
              className="mb-4"
            >
              <Link 
                to="/courses" 
                className={`
                  inline-block bg-green-600 hover:bg-green-700 text-white 
                  ${isMobile 
                    ? 'px-8 py-4 text-lg rounded-xl min-h-[48px] min-w-[140px]' 
                    : 'px-6 py-3 text-lg rounded-lg'
                  }
                  font-medium shadow-lg hover:shadow-xl transition-all duration-200
                  active:shadow-md
                `}
                style={{ 
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                {t.browseBtn}
              </Link>
            </motion.div>

            {/* نص مساعد للهواتف */}
            {isMobile && (
              <p className="text-sm text-gray-500 mt-2">
                {t.tapToExplore}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Stats Section - محسّن للهواتف */}
      <section className="py-8 sm:py-12 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 gap-4 sm:gap-6 text-center max-w-md mx-auto sm:max-w-none sm:grid-cols-2">
            {t.stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: isMobile ? 10 : 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: isMobile ? 0.4 : 0.5, 
                  delay: index * 0.1 
                }}
                viewport={{ once: true, margin: isMobile ? "-50px" : "-100px" }}
                className="p-4 sm:p-6 bg-white rounded-lg shadow-sm"
              >
                <p className="text-2xl sm:text-3xl font-bold text-green-600 mb-1">{stat.value}</p>
                <p className="text-sm sm:text-base text-gray-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - محسّن للشاشات الصغيرة */}
      <section className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-8 sm:mb-12 px-4">
            {language === 'ar' ? "كيف نساعدك؟" : "How We Help You"}
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {t.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: isMobile ? 10 : 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: isMobile ? 0.4 : 0.5, 
                  delay: index * 0.1 
                }}
                viewport={{ once: true, margin: isMobile ? "-30px" : "-50px" }}
                className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
                style={{ 
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                <div className="text-2xl sm:text-3xl mb-3 text-center">{feature.icon}</div>
                <h3 className="text-base sm:text-lg font-semibold mb-2 text-center">{feature.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 text-center leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - محسّن للهواتف */}
      <section className="py-12 sm:py-16 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-lg sm:text-2xl font-bold mb-6 px-4 leading-relaxed">{t.cta}</h2>
          
          {/* أزرار متعددة للهواتف */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <motion.div
              whileHover={!isMobile ? { scale: 1.03 } : {}}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                to="/courses" 
                className={`
                  inline-block bg-green-600 hover:bg-green-700 text-white
                  ${isMobile 
                    ? 'px-8 py-4 text-lg rounded-xl min-h-[48px] w-full sm:w-auto' 
                    : 'px-6 py-3 text-lg rounded-lg'
                  }
                  font-medium shadow-lg hover:shadow-xl transition-all duration-200
                  active:shadow-md
                `}
                style={{ 
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                {t.browseBtn}
              </Link>
            </motion.div>
            
            {/* زر سريع للهواتف */}
            {isMobile && (
              <motion.div
                whileTap={{ scale: 0.95 }}
              >
                <Link 
                  to="/courses" 
                  className="inline-block border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white px-8 py-4 text-lg rounded-xl min-h-[48px] w-full sm:w-auto font-medium transition-all duration-200"
                  style={{ 
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  {t.quickStart}
                </Link>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Floating Action Button للهواتف */}
      {isMobile && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1, duration: 0.3 }}
          className="fixed bottom-6 right-6 z-50"
        >
          <Link
            to="/courses"
            className="flex items-center justify-center w-14 h-14 bg-green-600 hover:bg-green-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 active:scale-95"
            style={{ 
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent'
            }}
            aria-label={t.browseBtn}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </Link>
        </motion.div>
      )}
    </div>
  );
}

export default HomePage;